import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { CheckCircle2, X } from 'lucide-react';

/**
 * Transient confirmation banners (see the "Admin Users — table and controls" section and the "Client Management" section). They appear at the top of the page,
 * auto-dismiss after ~4 s and are announced to screen readers via `role="status"`.
 */

export interface Banner {
  id: number;
  text: string;
  tone: 'success' | 'danger';
}

interface BannerApi {
  show: (text: string, tone?: Banner['tone']) => void;
}

const BannerContext = createContext<BannerApi | null>(null);

export const BANNER_TIMEOUT_MS = 4000;

export function BannerProvider({ children }: { children: ReactNode }) {
  const [banners, setBanners] = useState<Banner[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setBanners((current) => current.filter((banner) => banner.id !== id));
  }, []);

  const show = useCallback(
    (text: string, tone: Banner['tone'] = 'success') => {
      const id = nextId.current++;
      setBanners((current) => [...current, { id, text, tone }]);
      window.setTimeout(() => dismiss(id), BANNER_TIMEOUT_MS);
    },
    [dismiss],
  );

  const api = useMemo<BannerApi>(() => ({ show }), [show]);

  return (
    <BannerContext.Provider value={api}>
      {children}
      {/*
        The live region is this container, which is on the page from the first render —
        not each banner as it arrives. A `role="status"` element inserted into the DOM
        already carrying its text is announced inconsistently across screen readers,
        because the region and its content appear in the same tick; a region that is
        already there and then changes is the case every implementation handles.
      */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 top-12 z-30 flex flex-col items-center gap-2 px-4"
      >
        {banners.map((banner) => (
          <div
            key={banner.id}
            className={
              'pointer-events-auto flex w-full max-w-xl items-center gap-2 rounded border px-3 py-2 text-[13px] shadow-sm ' +
              (banner.tone === 'success'
                ? 'border-hairline-strong bg-success-soft text-ink'
                : 'border-danger bg-danger-soft text-ink')
            }
          >
            {banner.tone === 'success' ? (
              <CheckCircle2 size={15} className="shrink-0 text-success" aria-hidden="true" />
            ) : null}
            <span className="flex-1">{banner.text}</span>
            <button
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dismiss(banner.id)}
              className="rounded p-0.5 text-ink-subtle hover:text-ink"
            >
              <X size={13} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </BannerContext.Provider>
  );
}

export function useBanners(): BannerApi {
  const context = useContext(BannerContext);
  if (!context) throw new Error('useBanners must be used inside a <BannerProvider>.');
  return context;
}
